import {
  Button,
  ButtonDropdown,
  SidebarCore,
  SkeletonLoader,
  type TDropdownItem,
  H3,
  Text,
} from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { AnimatePresence, motion } from 'motion/react';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';

import useGetChatAgentConversations from '@/api/queries/me/useGetChatAgentConversations';
import useIsContainerBeyondBreakpoint from '@/hooks/useIsContainerBeyondBreakpoint';
import { useChatAgent } from '@/ui/components/chatAgent/ChatAgentContextProvider';

import useGetMe from '@/api/queries/me/useGetMe';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';
import { PageContextBadges } from './PageContextBadges';
import { PendingMessage } from './PendingMessage';

const ChatAgent: FC = () => {
  const { user } = useGetMe();
  const bodyRef = useRef<HTMLDivElement>(null);
  // The panel sits above the route definitions, so useRouteMatch()/useParams()
  // would always match the root "/". useLocation() gives the current path.
  const location = useLocation();
  const isMdAndUp = useIsContainerBeyondBreakpoint('md', {
    container: 'document',
  });
  // Track whether to auto-scroll as streaming content arrives
  const shouldStickToBottom = useRef(true);
  const {
    show: isOpen,
    hideChatAgent,
    sendMessage,
    interruptAndSend,
    chat,
    inputRef,
    binding,
  } = useChatAgent();
  const {
    conversationId: activeConversationId,
    messages,
    isLoading,
    isStreaming,
    error,
    isDisabled,
    status,
    streamingConversationIds,
    queuedMessages,
    stopStreaming,
    cancelQueuedMessage,
    switchConversation,
  } = chat;
  const { chatAgentConversations, fetchNextPage, hasNextPage } =
    useGetChatAgentConversations();

  // Detect when user manually scrolls away from bottom
  // With flex-col-reverse, scrollTop near 0 = at bottom
  const handleScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const isAtBottom = el.scrollTop <= 50;
    shouldStickToBottom.current = isAtBottom;
  }, []);

  // Reset sticky scroll when switching conversations
  useEffect(() => {
    shouldStickToBottom.current = true;
  }, [activeConversationId]);

  // Auto-scroll to bottom when messages update during streaming
  useEffect(() => {
    if (isStreaming && shouldStickToBottom.current) {
      bodyRef.current?.scrollTo({ top: 0 });
    }
  }, [messages, isStreaming]);

  // Send from the input box into the active conversation, then jump to the
  // bottom. Page context and the stream call are handled by the shared
  // `sendMessage` from the chat agent context.
  const handleInputSend = useCallback(
    (message: string) => {
      shouldStickToBottom.current = true;
      bodyRef.current?.scrollTo({ top: 0 });
      sendMessage({ prompt: message });
    },
    [sendMessage]
  );

  const handleInterruptAndSend = useCallback(
    (message: string) => {
      shouldStickToBottom.current = true;
      bodyRef.current?.scrollTo({ top: 0 });
      interruptAndSend(message);
    },
    [interruptAndSend]
  );

  // Todo: title from the useChatStream hook does not update until you switch conversations - fix that
  const currentConversationTitle = chatAgentConversations?.find(
    (i) => i.id === activeConversationId
  )?.title;

  const conversationDropdownItems = useMemo<TDropdownItem[]>(() => {
    const newChatItem: TDropdownItem = {
      label: '+ New chat',
      searchKey: 'New chat',
      selected: activeConversationId === null,
      onClick: () => {
        switchConversation(null);
        inputRef.current?.focus();
      },
    };

    const conversationItems: TDropdownItem[] =
      chatAgentConversations?.map((conversation) => ({
        label: conversation.title || 'Untitled conversation',
        searchKey: conversation.title || 'Untitled conversation',
        selected: conversation.id === activeConversationId,
        isProcessing: streamingConversationIds.has(conversation.id),
        onClick: () => {
          switchConversation(conversation.id);
          inputRef.current?.focus();
        },
      })) ?? [];

    return [newChatItem, ...conversationItems];
  }, [
    activeConversationId,
    chatAgentConversations,
    streamingConversationIds,
    switchConversation,
    inputRef,
  ]);

  const greeting = binding?.greeting ?? [
    `Hi ${user?.firstName ?? 'there'}, I'm Agent Dave 👋`,
    'Ask me anything',
    "I have access to Payaca's help docs and can get a lot done for you",
    <>
      If you need a human, click the{' '}
      <UntitledIcon className="size-5 align-middle" name="help-circle.3" />{' '}
      below at any point
    </>,
  ];

  const panelInner: ReactNode = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-2 border-b shrink-0">
        <ButtonDropdown
          variant="quaternary"
          size="xs"
          dropdownOptions={{
            items: conversationDropdownItems,
            hasSearch: true,
            anchor: 'start',
            footer: hasNextPage ? (
              <button
                type="button"
                className="w-full p-2 text-sm text-gray-500 hover:bg-gray-50"
                onClick={() => void fetchNextPage()}
              >
                Load more...
              </button>
            ) : undefined,
          }}
        >
          {currentConversationTitle ||
            binding?.newConversationTitle ||
            'New chat'}
          <UntitledIcon name="chevron-selector-vertical" className="size-4" />
        </ButtonDropdown>
        <Button onClick={hideChatAgent} variant="quaternary" size="sm">
          <span className="sr-only">Close chat panel</span>
          <UntitledIcon
            name={isMdAndUp ? 'chevron-right-double.3' : 'x-close'}
            className="size-5"
          />
        </Button>
      </div>

      {/* Messages area */}
      <AnimatePresence mode="wait">
        <motion.div
          ref={bodyRef}
          key={activeConversationId}
          className="flex-1 flex flex-col-reverse gap-4 p-4 overflow-y-auto min-w-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onScroll={handleScroll}
        >
          {/* Queued messages sit below the transcript, nearest the composer.
              In flex-col-reverse the first DOM child renders at the bottom, so
              reverse the FIFO queue to keep the next-to-send message highest. */}
          {queuedMessages.toReversed().map((queued) => (
            <PendingMessage
              key={queued.id}
              message={queued}
              onCancel={cancelQueuedMessage}
            />
          ))}

          {isLoading && <SkeletonLoader className="h-10 w-full" />}

          {messages.length === 0 && !isLoading && (
            <div className="text-center m-auto max-w-72 space-y-1.5">
              <H3>{greeting[0]}</H3>
              {greeting.slice(1).map((line, index) => (
                <Text key={index} colour="muted">
                  {line}
                </Text>
              ))}
            </div>
          )}
          {messages.toReversed().map((msg) => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          {error && (
            <div className="rounded bg-red-100 p-2 text-sm text-red-600">
              {error}
            </div>
          )}
          {status === 'compacting' && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              Conversation limit reached. Compacting so we can continue...
            </div>
          )}
          {status === 'retrying' && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
              Something went wrong. Retrying...
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Footer */}
      <div className="p-4 pt-0 shrink-0">
        <PageContextBadges pathname={location.pathname} />
        <ChatInput
          ref={inputRef}
          onSend={handleInputSend}
          onStop={stopStreaming}
          onInterruptAndSend={handleInterruptAndSend}
          isStreaming={isStreaming}
          disabled={isDisabled}
          messages={messages}
          placeholder={binding?.inputPlaceholder}
        />
      </div>
    </>
  );

  if (!isMdAndUp) {
    // The bottom-sheet container has a fixed height but the DialogPanel inside
    // it does not stretch by default, so without these classes the panel sizes
    // to its content. That breaks the flex-1 messages area: it has no bounded
    // height to scroll within, so a second conversation turn overflows the
    // sheet, pushing the input off-screen and leaving the messages
    // unscrollable.
    return (
      <SidebarCore
        className="flex h-full min-h-0 flex-col"
        isOpen={isOpen}
        onClose={hideChatAgent}
        size="standard"
      >
        {panelInner}
      </SidebarCore>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="chat-panel"
          initial={{ width: 0 }}
          animate={{ width: 'max(350px, 25vw)' }}
          exit={{ width: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="h-full overflow-hidden shrink-0 grid grid-rows-1"
        >
          <aside
            aria-label="Chat with Dave"
            className="flex flex-col min-w-0 min-h-0 bg-white border m-2 rounded-lg h-auto"
            style={{ width: 'calc(max(350px, 25vw) - 1rem)' }}
          >
            {panelInner}
          </aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChatAgent;
